import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
} from "react";
import { resolveViewerShortcut } from "./viewer-shortcuts";
import { matchesUndoRedoShortcut } from "./workbook-history-ops";

interface UseViewerShortcutsParams {
  viewerRef: RefObject<HTMLDivElement | null>;
  isMaximized: boolean;
  setIsMaximized: Dispatch<SetStateAction<boolean>>;
  isSheetSwitcherOpen: boolean;
  setIsSheetSwitcherOpen: Dispatch<SetStateAction<boolean>>;
  canOpenSheetSearch: boolean;
  openSheetSearch: () => void;
}

export const useViewerShortcuts = ({
  viewerRef,
  isMaximized,
  setIsMaximized,
  isSheetSwitcherOpen,
  setIsSheetSwitcherOpen,
  canOpenSheetSearch,
  openSheetSearch,
}: UseViewerShortcutsParams) => {
  const isViewerShortcutScopeActive = useCallback(() => {
    if (isMaximized || isSheetSwitcherOpen) return true;

    const activeElement = document.activeElement;
    return (
      activeElement instanceof Node &&
      (viewerRef.current?.contains(activeElement) ?? false)
    );
  }, [isMaximized, isSheetSwitcherOpen]);

  useEffect(() => {
    const handleViewerShortcut = (event: KeyboardEvent) => {
      const action = resolveViewerShortcut(event, {
        canOpenSheetSearch,
        isViewerActive: isViewerShortcutScopeActive(),
      });
      if (!action) return;

      event.preventDefault();
      switch (action) {
        case "open-sheet-search":
          openSheetSearch();
          break;
        case "toggle-maximized":
          setIsMaximized((prev) => !prev);
          break;
        case "toggle-sheet-switcher":
          setIsSheetSwitcherOpen((prev) => !prev);
          break;
      }
    };

    document.addEventListener("keydown", handleViewerShortcut);
    return () => document.removeEventListener("keydown", handleViewerShortcut);
  }, [canOpenSheetSearch, isViewerShortcutScopeActive, openSheetSearch]);

  return isViewerShortcutScopeActive;
};

interface UseUndoRedoShortcutParams {
  isViewerShortcutScopeActive: () => boolean;
  undo: () => void;
  redo: () => void;
}

export const useUndoRedoShortcut = ({
  isViewerShortcutScopeActive,
  undo,
  redo,
}: UseUndoRedoShortcutParams) => {
  useEffect(() => {
    const handleUndoRedoShortcut = (event: KeyboardEvent) => {
      const action = matchesUndoRedoShortcut(event);
      if (!action) return;
      if (event.defaultPrevented || !isViewerShortcutScopeActive()) return;

      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          active.isContentEditable)
      ) {
        return;
      }

      event.preventDefault();
      if (action === "undo") {
        undo();
      } else {
        redo();
      }
    };

    document.addEventListener("keydown", handleUndoRedoShortcut);
    return () =>
      document.removeEventListener("keydown", handleUndoRedoShortcut);
  }, [isViewerShortcutScopeActive, undo, redo]);
};
